import type { KrLang } from './types';

/**
 * Flagship deep dive: Mona Yongpyong (모나용평, formerly Yongpyong Resort).
 *
 * Korea's equivalent of src/data/furanoDeepDive.ts plus the copy that the
 * Japan site keeps inline in FuranoPage.tsx, FuranoGuidePage.tsx and
 * FuranoFaqPage.tsx. site.config `routes.flagship = { slug: 'yongpyong',
 * resortSlug: 'yongpyong' }` mounts them at /{lang}/yongpyong/,
 * /{lang}/yongpyong/travel-guide/ and /{lang}/yongpyong/faq/.
 *
 * Figures are from the resort's own pages read on 2026-10-20 (slope table,
 * lift-fee page, cable car page, ski school, dining, KTX and shuttle pages),
 * FIS/Olympedia for the 2018 Olympic course, and the shuttle operators'
 * 2026-27 booking pages. Prices are the 2025-26 season's; 2026-27 prices were
 * "to be announced" on the resort site at the time of writing, and the copy
 * says so. Sources: sites/kr/GUIDES_SOURCES.md.
 */

export interface DeepDiveSection {
  id: string;
  heading: string;
  body: string;
  bullets?: string[];
}

export interface DeepDiveCopy {
  heading: string;
  intro: string;
  sections: DeepDiveSection[];
}

/** Same shape as the inline COPY in src/pages/FuranoPage.tsx. */
export interface FlagshipPageCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  factsHeading: string;
  facts: { label: string; value: string }[];
  zonesHeading: string;
  zones: { name: string; body: string }[];
  snowHeading: string;
  snowBody: string;
  ticketHeading: string;
  ticketBody: string;
  accessHeading: string;
  accessBody: string;
  linksHeading: string;
  links: { to: string; label: string; icon: 'plane' | 'ticket' | 'camera' | 'calendar' }[];
  faq: { q: string; a: string }[];
}

/** Same shape as the inline COPY in src/pages/FuranoGuidePage.tsx. */
export interface FlagshipGuideCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  sections: { icon: 'plane' | 'hotel' | 'utensils' | 'calendar'; heading: string; body: string[] }[];
  linksHeading: string;
  links: { to: string; label: string }[];
}

/** Same shape as the inline COPY in src/pages/FuranoFaqPage.tsx. */
export interface FlagshipFaqCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  lede: string;
  faq: { q: string; a: string }[];
}

export const FLAGSHIP_RESORT_SLUG = 'yongpyong';

export const YONGPYONG_DEEP_DIVE: Record<KrLang, DeepDiveCopy> = {
  ko: {
    heading: '용평 여행 계획: 개장·눈·렌탈·현지 이동',
    intro: '용평은 슬로프 구역마다 성격이 뚜렷하고 운영 시간도 다릅니다. 개장 방식, 눈 사정, 렌탈 요금, 현지 이동과 식사까지 일정을 짜는 데 필요한 부분을 정리했습니다.',
    sections: [
      {
        id: 'dates',
        heading: '개장 방식과 운영 시간',
        body: '용평은 국내에서 가장 먼저 문을 여는 리조트 중 하나입니다. 2025-26 시즌에는 11월 21일 핑크 슬로프부터 열고 나머지 슬로프를 단계적으로 개방했습니다. 2026-27 개장일은 아직 발표되지 않았으니 공지를 확인하세요. 구역마다 마감 시간이 달라 레인보우 구역은 16:00, 골드 구역은 16:30, 그린 구역은 17:00에 끝나고, 야간 스키는 19:00~23:00입니다.',
        bullets: [
          '2025-26 개장: 11월 21일 (핑크 슬로프부터 단계적 개방)',
          '레인보우 파라다이스·레인보우1: 09:00~16:00',
          '골드 구역: 09:00~16:30 / 그린 구역: 09:00~17:00',
          '야간: 19:00~23:00 (17:00~19:00 정설 시간)',
        ],
      },
      {
        id: 'snow',
        heading: '눈 사정',
        body: '용평은 대관령 고원에 자리해 국내에서도 손꼽히게 추운 곳입니다. 다른 국내 리조트처럼 개장은 인공 제설로 시작하고, 동풍이 동해의 습기를 태백산맥으로 밀어 올릴 때 자연설이 크게 쌓입니다. 1월과 2월 초가 가장 추워 설면이 오래 유지되고, 정상(발왕산 1,458m)과 레인보우 구역은 바람이 강한 날 운행이 멈출 수 있으니 출발 전 라이브 캠을 확인하세요.',
      },
      {
        id: 'rentals',
        heading: '렌탈과 강습',
        body: '리조트 공식 렌탈 기준으로 일반 스키·보드 풀세트는 주간권 사용 시 48,000원, 반일·야간권 사용 시 40,000원이었습니다(2025-26). 프리미엄 스키 세트는 67,000원/63,000원, 의류 상하는 1회 20,000원부터입니다. 모나용평 스키스쿨에는 영어가 가능한 강사가 맡는 외국인 전담반이 있으며, 리조트 인증 스쿨 외의 개인 강습은 허용되지 않습니다.',
        bullets: [
          '일반 풀세트: 주간 48,000원 / 반일·야간 40,000원',
          '프리미엄 스키 세트: 주간 67,000원 / 반일·야간 63,000원',
          '의류 상하: 1회 20,000원 ~ 4일 90,000원',
          '외국인 전담반 (영어 강사) · 스키스쿨 033-330-7373',
          '2026-27 요금은 시즌 전 공지 예정',
        ],
      },
      {
        id: 'localtips',
        heading: '현지 이동과 식사',
        body: '리조트 안에서는 대부분 걸어서 다니며, 아랫마을 횡계까지는 차로 금방입니다. 횡계는 황태(말린 명태)의 고장으로 황태해장국과 황태구이를 내는 식당이 모여 있고, 오징어와 삼겹살을 함께 볶는 오삼불고기 거리도 있습니다. 리조트 안에서는 드래곤밸리호텔의 더 샬레, 한우 구이집 삼부자1983, 타워플라자 푸드코트를 이용할 수 있습니다. 쉬는 날에는 발왕산 케이블카, 대관령 양떼목장, KTX로 20분 남짓인 강릉의 바다와 커피 거리를 추천합니다.',
        bullets: [
          '횡계 명물: 황태해장국, 황태구이, 오삼불고기',
          '리조트 내: 더 샬레(호텔 1층), 삼부자1983, 타워플라자 푸드코트',
          '쉬는 날: 발왕산 케이블카·스카이워크, 대관령 양떼목장, 강릉',
          '진부역 ↔ 리조트 투숙객 셔틀 하루 2회, 택시 약 20분',
        ],
      },
    ],
  },
  en: {
    heading: 'Planning Yongpyong: opening, snow, rentals and getting around',
    intro: 'Each zone at Yongpyong has its own character and its own closing time. Here is how the season opens, what the snow is like, what rentals cost and how to get around and eat once you are there.',
    sections: [
      {
        id: 'dates',
        heading: 'How the season opens, and daily hours',
        body: 'Yongpyong is one of the first Korean resorts to open. In 2025-26 it opened on November 21 with the Pink slope and added the rest in stages; the 2026-27 date had not been announced at the time of writing. Zones close at different times (Rainbow at 16:00, Gold at 16:30, Green at 17:00), and night skiing runs 19:00 to 23:00.',
        bullets: [
          '2025-26 opening: November 21, starting with the Pink slope',
          'Rainbow Paradise and Rainbow 1: 09:00–16:00',
          'Gold zone: 09:00–16:30 / Green zone: 09:00–17:00',
          'Night: 19:00–23:00 (grooming 17:00–19:00)',
        ],
      },
      {
        id: 'snow',
        heading: 'What the snow is like',
        body: 'Yongpyong sits on the Daegwallyeong plateau, one of the coldest places in South Korea. Like every Korean resort it opens on machine-made snow; the big natural falls come when easterly winds push East Sea moisture up against the Taebaek range. January and early February are the coldest weeks, when the surface holds longest. The summit (Balwangsan, 1,458 m) and the Rainbow zone can close in high wind, so check the live cams before heading up.',
      },
      {
        id: 'rentals',
        heading: 'Rentals and lessons',
        body: 'At the resort\'s own rental shop a standard ski or board set was ₩48,000 with a day ticket and ₩40,000 with a half-day or night ticket in 2025-26. A premium ski set was ₩67,000/₩63,000, and jacket and pants start at ₩20,000 a session. Mona Yongpyong Ski School runs a dedicated foreigners\' class with English-speaking instructors; only the resort-certified schools may teach on the slopes.',
        bullets: [
          'Standard set: ₩48,000 day / ₩40,000 half-day or night',
          'Premium ski set: ₩67,000 day / ₩63,000 half-day or night',
          'Jacket and pants: ₩20,000 a session up to ₩90,000 for 4 days',
          'Foreigners\' class with English-speaking instructors · school 033-330-7373',
          '2026-27 prices to be announced before the season',
        ],
      },
      {
        id: 'localtips',
        heading: 'Getting around and eating',
        body: 'Inside the resort you walk almost everywhere; Hoenggye, the town below, is a short drive. Hoenggye is dried-pollack (hwangtae) country, with restaurants serving hwangtae hangover soup and grilled hwangtae, plus a street of osam bulgogi (squid and pork belly stir-fry). On the resort, The Chalet in Dragon Valley Hotel, the Korean-beef grill Sambuja 1983 and the Tower Plaza food court cover most needs. For a rest day: the Balwangsan cable car, Daegwallyeong Sheep Farm, or Gangneung\'s coast and coffee street, about 20 minutes on from Jinbu by KTX.',
        bullets: [
          'Hoenggye specialities: hwangtae soup, grilled hwangtae, osam bulgogi',
          'On the resort: The Chalet (hotel 1F), Sambuja 1983, Tower Plaza food court',
          'Rest day: Balwangsan cable car and skywalk, sheep farm, Gangneung',
          'Jinbu Station ↔ resort: guest shuttle twice a day, taxi about 20 minutes',
        ],
      },
    ],
  },
  ja: {
    heading: '龍平の計画：オープン・雪・レンタル・現地の動き方',
    intro: '龍平はゾーンごとに性格がはっきりしていて、営業時間も異なります。オープンの流れ、雪の状況、レンタル料金、現地での移動と食事まで、旅程づくりに必要な部分をまとめました。',
    sections: [
      {
        id: 'dates',
        heading: 'オープンの流れと営業時間',
        body: '龍平は韓国で最も早くオープンするスキー場のひとつです。2025-26シーズンは11月21日にピンクコースから営業を始め、残りのコースを段階的に開きました。2026-27のオープン日はまだ発表されていません。ゾーンごとに終了時刻が異なり、レインボーは16:00、ゴールドは16:30、グリーンは17:00まで。ナイターは19:00〜23:00です。',
        bullets: [
          '2025-26オープン：11月21日（ピンクコースから段階的に開放）',
          'レインボーパラダイス・レインボー1：09:00〜16:00',
          'ゴールドゾーン：09:00〜16:30／グリーンゾーン：09:00〜17:00',
          'ナイター：19:00〜23:00（17:00〜19:00は整備時間）',
        ],
      },
      {
        id: 'snow',
        heading: '雪の状況',
        body: '龍平は韓国でも指折りの寒さで知られる大関嶺の高原にあります。韓国のほかのスキー場と同じく人工降雪でオープンし、東風が東海（日本海）の湿った空気を太白山脈に押し上げるときにまとまった自然雪が降ります。1月から2月上旬が最も寒く、雪面が長持ちします。山頂（発旺山1,458m）とレインボーゾーンは強風で運休することがあるので、出発前にライブカメラを確認しましょう。',
      },
      {
        id: 'rentals',
        heading: 'レンタルとレッスン',
        body: 'リゾート直営のレンタルでは、標準のスキー・ボードセットが1日券利用時48,000ウォン、半日・ナイター券利用時40,000ウォンでした（2025-26）。プレミアムスキーセットは67,000／63,000ウォン、ウェア上下は1回20,000ウォンから。モナ龍平スキースクールには英語の話せるインストラクターが担当する外国人専用クラスがあり、リゾート認定スクール以外の個人レッスンは認められていません。',
        bullets: [
          '標準セット：1日48,000ウォン／半日・ナイター40,000ウォン',
          'プレミアムスキーセット：1日67,000ウォン／半日・ナイター63,000ウォン',
          'ウェア上下：1回20,000ウォン〜4日90,000ウォン',
          '外国人専用クラス（英語インストラクター）・スクール033-330-7373',
          '2026-27の料金はシーズン前に発表予定',
        ],
      },
      {
        id: 'localtips',
        heading: '現地の動き方と食事',
        body: 'リゾート内はほぼ徒歩で移動でき、麓の町・横渓（フェンゲ）へは車ですぐです。横渓は干しスケトウダラ（ファンテ）の産地で、ファンテのスープや焼き物を出す店が集まり、イカと豚バラを炒めるオサムプルコギの通りもあります。リゾート内ではドラゴンバレーホテルの「ザ・シャレー」、韓牛焼肉の「サムブジャ1983」、タワープラザのフードコートが便利です。休養日には発旺山ケーブルカー、大関嶺の羊牧場、珍富からKTXで20分ほどの江陵の海とコーヒー通りがおすすめです。',
        bullets: [
          '横渓名物：ファンテのスープ、ファンテ焼き、オサムプルコギ',
          'リゾート内：ザ・シャレー（ホテル1階）、サムブジャ1983、タワープラザのフードコート',
          '休養日：発旺山ケーブルカーとスカイウォーク、羊牧場、江陵',
          '珍富駅⇔リゾート：宿泊者シャトル1日2便、タクシー約20分',
        ],
      },
    ],
  },
  zh: {
    heading: '规划龙平之旅：开放、雪况、租赁与当地交通',
    intro: '龙平每个雪区特点鲜明，营业时间也各不相同。这里整理了开放方式、雪况、租赁价格以及到达后的交通和餐饮，帮助你安排行程。',
    sections: [
      {
        id: 'dates',
        heading: '开放方式与营业时间',
        body: '龙平是韩国最早开放的滑雪场之一。2025-26雪季于11月21日从粉红雪道开始营业，其余雪道分阶段开放；2026-27开放日期截至撰稿时尚未公布。各雪区关闭时间不同：彩虹区16:00、黄金区16:30、绿色区17:00，夜场为19:00至23:00。',
        bullets: [
          '2025-26开放：11月21日（从粉红雪道开始分阶段开放）',
          '彩虹天堂与彩虹1号：09:00–16:00',
          '黄金区：09:00–16:30／绿色区：09:00–17:00',
          '夜场：19:00–23:00（17:00–19:00为压雪时间）',
        ],
      },
      {
        id: 'snow',
        heading: '雪况如何',
        body: '龙平位于大关岭高原，是韩国最寒冷的地方之一。与韩国其他滑雪场一样，开放初期依靠人工造雪；当东风把东海（日本海）的湿气推向太白山脉时，会迎来大量自然降雪。1月至2月上旬最冷，雪面保持时间最长。山顶（发旺山1,458米）和彩虹区在大风天可能停运，出发前请查看实时摄像头。',
      },
      {
        id: 'rentals',
        heading: '租赁与教学',
        body: '在度假村直营租赁店，2025-26雪季标准双板或单板套装配合全天票为48,000韩元，配合半天或夜场票为40,000韩元。高级双板套装为67,000/63,000韩元，雪服上下装每次20,000韩元起。Mona龙平滑雪学校开设由会说英语的教练负责的外国人专班；只有度假村认证的学校可以在雪道上授课。',
        bullets: [
          '标准套装：全天48,000韩元／半天或夜场40,000韩元',
          '高级双板套装：全天67,000韩元／半天或夜场63,000韩元',
          '雪服上下装：每次20,000韩元起，4天90,000韩元',
          '外国人专班（英语教练）· 滑雪学校033-330-7373',
          '2026-27价格将在雪季前公布',
        ],
      },
      {
        id: 'localtips',
        heading: '当地交通与餐饮',
        body: '度假村内基本步行即可，山下的横溪镇开车很快就到。横溪是黄太（干明太鱼）之乡，有不少供应黄太醒酒汤和烤黄太的餐厅，还有一条炒鱿鱼五花肉（오삼불고기）街。度假村内可以去龙谷酒店的The Chalet、韩牛烤肉店三父子1983和Tower Plaza美食广场。休息日推荐发旺山缆车、大关岭牧羊场，或从珍富乘KTX约20分钟到江陵看海、逛咖啡街。',
        bullets: [
          '横溪特色：黄太醒酒汤、烤黄太、炒鱿鱼五花肉',
          '度假村内：The Chalet（酒店一层）、三父子1983、Tower Plaza美食广场',
          '休息日：发旺山缆车与天空步道、牧羊场、江陵',
          '珍富站⇔度假村：住客班车每天2班，打车约20分钟',
        ],
      },
    ],
  },
};

/** Generic alias so shared flagship pages can import one name per site. */
export const FLAGSHIP_DEEP_DIVE = YONGPYONG_DEEP_DIVE;

export const YONGPYONG_PAGE_COPY: Record<KrLang, FlagshipPageCopy> = {
  ko: {
    title: '모나용평 가이드 | 라이브 캠·슬로프·리프트권·가는 법',
    description: '모나용평(용평리조트) 완전 가이드. 라이브 캠, 28개 슬로프와 5.6km 레인보우 파라다이스, 2018 올림픽 코스, 리프트권 요금, 서울·인천공항·KTX 진부역에서 가는 법을 정리했습니다.',
    keywords: '용평리조트, 모나용평, 용평 스키장, 용평 리프트권, 용평 셔틀버스, 레인보우 파라다이스, 발왕산 케이블카',
    h1: '용평리조트(모나용평) 완전 가이드',
    lede: '용평은 1975년 문을 연 한국 최초의 현대식 스키장으로, 평창 대관령 고원의 발왕산 자락(슬로프 해발 745~1,450m)에 28개 슬로프와 14기의 리프트를 갖춘 국내 대표 리조트입니다. 5.6km의 레인보우 파라다이스와 2018 평창 올림픽 회전·대회전 코스가 있고, KTX 진부역에서 차로 약 20분입니다.',
    factsHeading: '기본 정보',
    facts: [
      { label: '위치', value: '강원특별자치도 평창군 대관령면 올림픽로 715' },
      { label: '정상', value: '발왕산 1,458m (케이블카 상부), 슬로프 최고점 1,450m' },
      { label: '슬로프·리프트', value: '28면 / 14기 (발왕산 관광 케이블카 포함)' },
      { label: '최장 슬로프', value: '레인보우 파라다이스 5.6km (표고차 705m)' },
      { label: '올림픽', value: '2018 평창 알파인 기술 종목 (레인보우1)' },
      { label: '가까운 역', value: 'KTX 진부(오대산)역, 택시 약 20분' },
    ],
    zonesHeading: '구역별 특징',
    zones: [
      { name: '레인보우 구역', body: '정상부의 상급 코스 지대입니다. 레인보우1~3은 FIS 공인 상급 코스로 2018 올림픽 회전·대회전이 레인보우1에서 열렸고, 레인보우 파라다이스(5.6km)는 정상에서 내려오는 긴 중급 코스입니다. 16:00에 마감합니다.' },
      { name: '골드·실버 구역', body: '골드 파라다이스(1,520m), 골드 판타스틱(1,850m), 실버 파라다이스(1,460m) 등 길고 넓은 중급 코스가 모여 있습니다. 뉴골드와 실버는 상급자 전용입니다.' },
      { name: '레드·그린 구역', body: '베이스에서 보이는 상징적인 레드 코스(최상급)와 폭 180m의 메가 그린이 있습니다. 메가 그린은 6인승 고속 리프트로 오르는 초중급 코스입니다.' },
      { name: '옐로·핑크와 드래곤파크', body: '옐로·뉴옐로·핑크는 초보자 코스이며, 옐로 리프트 옆에 어린이 스키스쿨 전용 공간이 있습니다. 드래곤파크는 500m 길이의 터레인 파크입니다.' },
    ],
    snowHeading: '눈과 붐비는 시기',
    snowBody: '대관령 고원은 국내에서 가장 추운 지역 중 하나라 1월~2월 초에 설면이 가장 안정적입니다. 개장은 인공 제설로 하고, 동풍이 부는 날 동해 쪽 습기가 많은 자연설을 가져옵니다. 주말과 설 연휴에는 리프트와 셔틀이 붐비니 평일을 노리고, 강풍이 부는 날은 케이블카와 레인보우 구역 운행 여부를 먼저 확인하세요.',
    ticketHeading: '리프트권',
    ticketBody: '2025-26 시즌 성인 주간권(09:00~17:00)은 96,000원, 오전·오후·야간권은 68,000원, 어린이(초등학생 이하)는 각각 78,000원·56,000원이었습니다. 2026-27 요금은 시즌 전에 공지됩니다. 자주 탄다면 용평·하이원·알펜시아 등 6개 리조트에서 쓰는 X6+ 시즌패스도 고려해 보세요.',
    accessHeading: '가는 법',
    accessBody: '서울에서는 홍대·신촌·명동·동대문에서 출발하는 스키 셔틀(2026년 11월 27일~2027년 3월 1일)이, 인천공항에서는 시즌 한정 공항 셔틀(2026년 12월 7일~2027년 2월 12일)이 다닙니다. KTX 강릉선 진부역에서는 투숙객 셔틀이 하루 2회 운행하고 택시로 약 20분입니다. 시외버스로 횡계까지 간 뒤 택시를 타도 됩니다.',
    linksHeading: '용평 더 알아보기',
    links: [
      { to: '/resort/yongpyong/', label: '용평 라이브 캠과 적설', icon: 'camera' },
      { to: '/season-calendar/', label: '개장·폐장 시기 확인', icon: 'calendar' },
    ],
    faq: [
      { q: '용평리조트는 언제 개장하나요?', a: '2025-26 시즌에는 11월 21일 개장했습니다. 2026-27 개장일은 아직 발표되지 않았으며, 보통 일부 슬로프부터 단계적으로 엽니다.' },
      { q: '초보자도 탈 수 있나요?', a: '네. 옐로·뉴옐로·핑크 코스와 폭넓은 메가 그린이 있고, 스키스쿨에 영어 강사가 맡는 외국인 전담반이 있습니다.' },
      { q: '올림픽 코스를 직접 탈 수 있나요?', a: '레인보우 구역이 열리면 2018 올림픽 회전·대회전이 열린 레인보우1을 탈 수 있습니다. 상급자 전용이며 16:00에 마감합니다.' },
    ],
  },
  en: {
    title: 'Yongpyong Ski Resort Guide: Webcams, Slopes, Lift Tickets',
    description: 'The complete guide to Mona Yongpyong: live webcams, 28 slopes including the 5.6 km Rainbow Paradise, the 2018 Olympic course, lift ticket prices and how to get there from Seoul, Incheon Airport and KTX Jinbu.',
    keywords: 'yongpyong ski resort, mona yongpyong, yongpyong lift ticket, seoul to yongpyong, yongpyong shuttle bus, rainbow paradise, pyeongchang skiing',
    h1: 'Yongpyong (Mona Yongpyong): complete guide',
    lede: 'Opened in 1975 as Korea\'s first modern ski resort, Yongpyong spreads 28 slopes and 14 lifts across the slopes of Balwangsan (ski area 745 to 1,450 m) on the Daegwallyeong plateau in Pyeongchang. It has the 5.6 km Rainbow Paradise and the slope that hosted the 2018 Olympic slalom and giant slalom, and it is about 20 minutes by road from KTX Jinbu Station.',
    factsHeading: 'The numbers',
    facts: [
      { label: 'Location', value: '715 Olympic-ro, Daegwallyeong-myeon, Pyeongchang, Gangwon' },
      { label: 'Summit', value: 'Balwangsan 1,458 m (cable car top station); top of the ski area 1,450 m' },
      { label: 'Slopes and lifts', value: '28 slopes / 14 lifts, including the Balwangsan cable car' },
      { label: 'Longest run', value: 'Rainbow Paradise, 5.6 km (705 m vertical)' },
      { label: 'Olympics', value: '2018 PyeongChang alpine technical events (Rainbow 1)' },
      { label: 'Nearest station', value: 'KTX Jinbu (Odaesan), about 20 minutes by taxi' },
    ],
    zonesHeading: 'The zones',
    zones: [
      { name: 'Rainbow zone', body: 'The high, advanced terrain. Rainbow 1 to 3 are FIS-homologated expert runs (the 2018 Olympic slalom and giant slalom ran on Rainbow 1), and Rainbow Paradise (5.6 km) is the long intermediate run from the top. Closes at 16:00.' },
      { name: 'Gold and Silver', body: 'Long, wide intermediate runs: Gold Paradise (1,520 m), Gold Fantastic (1,850 m) and Silver Paradise (1,460 m). New Gold and Silver are expert-only.' },
      { name: 'Red and Green', body: 'The landmark Red run seen from the base (expert) and the 180 m-wide Mega Green, an easy cruiser served by a six-seat high-speed lift.' },
      { name: 'Yellow, Pink and Dragon Park', body: 'Yellow, New Yellow and Pink are the beginner slopes, with a dedicated kids\' ski school area by the Yellow lift. Dragon Park is a 500 m terrain park.' },
    ],
    snowHeading: 'Snow and crowds',
    snowBody: 'The Daegwallyeong plateau is one of the coldest places in South Korea, so January and early February bring the most stable snow. The resort opens on machine-made snow; easterly winds bring the big natural falls from the East Sea side. Weekends and the Seollal holiday crowd the lifts and shuttles, so aim for weekdays, and on windy days check whether the cable car and Rainbow zone are running.',
    ticketHeading: 'Lift tickets',
    ticketBody: 'In 2025-26 an adult day ticket (09:00–17:00) was ₩96,000 and a morning, afternoon or night ticket ₩68,000; children (elementary age and under) paid ₩78,000 and ₩56,000. 2026-27 prices will be announced before the season. If you ski a lot, the X6+ Season Pass covers Yongpyong, High1, Alpensia and three other resorts.',
    accessHeading: 'Getting there',
    accessBody: 'From Seoul, ski shuttles leave Hongdae, Sinchon, Myeongdong and Dongdaemun (November 27, 2026 to March 1, 2027); from Incheon Airport there is a season-only shuttle (December 7, 2026 to February 12, 2027). From KTX Jinbu Station a guest shuttle runs twice a day, or take a taxi (about 20 minutes). An intercity bus to Hoenggye plus a taxi also works.',
    linksHeading: 'More on Yongpyong',
    links: [
      { to: '/resort/yongpyong/', label: 'Yongpyong live webcams and snow data', icon: 'camera' },
      { to: '/season-calendar/', label: 'Check opening and closing dates', icon: 'calendar' },
    ],
    faq: [
      { q: 'When does Yongpyong open?', a: 'It opened on November 21 in 2025-26. The 2026-27 date has not been announced; the resort usually opens a few slopes first and adds the rest in stages.' },
      { q: 'Is Yongpyong good for beginners?', a: 'Yes. Yellow, New Yellow and Pink plus the wide Mega Green suit beginners, and the ski school runs a foreigners\' class with English-speaking instructors.' },
      { q: 'Can I ski the Olympic course?', a: 'When the Rainbow zone is open you can ride Rainbow 1, where the 2018 Olympic slalom and giant slalom were held. It is expert-only and closes at 16:00.' },
    ],
  },
  ja: {
    title: '龍平リゾート ガイド｜ライブカメラ・コース・リフト券・アクセス',
    description: 'モナ龍平（龍平リゾート）の完全ガイド。ライブカメラ、全長5.6kmのレインボーパラダイスを含む28コース、2018年五輪コース、リフト券料金、ソウル・仁川空港・KTX珍富駅からのアクセスを解説します。',
    keywords: '龍平リゾート, ヨンピョン スキー場, モナ龍平, 龍平 リフト券, 龍平 シャトルバス, 平昌 スキー',
    h1: '龍平（ヨンピョン）リゾート 完全ガイド',
    lede: '龍平は1975年に開業した韓国初の本格的なスキー場で、平昌・大関嶺高原の発旺山の斜面（コース標高745〜1,450m）に28コースと14基のリフトを備える韓国を代表するリゾートです。全長5.6kmのレインボーパラダイスと、2018年平昌五輪の回転・大回転が行われたコースがあり、KTX珍富駅から車で約20分です。',
    factsHeading: '基本データ',
    facts: [
      { label: '所在地', value: '江原特別自治道 平昌郡 大関嶺面 オリンピック路715' },
      { label: '山頂', value: '発旺山 1,458m（ケーブルカー山頂駅）、コース最高点1,450m' },
      { label: 'コース・リフト', value: '28コース／14基（発旺山観光ケーブルカーを含む）' },
      { label: '最長コース', value: 'レインボーパラダイス 5.6km（標高差705m）' },
      { label: 'オリンピック', value: '2018年平昌五輪 アルペン技術系種目（レインボー1）' },
      { label: '最寄り駅', value: 'KTX珍富（オデサン）駅、タクシー約20分' },
    ],
    zonesHeading: 'ゾーンの特徴',
    zones: [
      { name: 'レインボーゾーン', body: '山頂部の上級者エリア。レインボー1〜3はFIS公認の上級コースで、2018年五輪の回転・大回転はレインボー1で行われました。レインボーパラダイス（5.6km）は山頂から下る長い中級コースです。16:00終了。' },
      { name: 'ゴールド・シルバー', body: 'ゴールドパラダイス（1,520m）、ゴールドファンタスティック（1,850m）、シルバーパラダイス（1,460m）など、長く幅の広い中級コースが集まります。ニューゴールドとシルバーは上級者専用です。' },
      { name: 'レッド・グリーン', body: 'ベースから見える象徴的なレッドコース（最上級）と、幅180mのメガグリーン。メガグリーンは6人乗り高速リフトで上がる初中級コースです。' },
      { name: 'イエロー・ピンクとドラゴンパーク', body: 'イエロー、ニューイエロー、ピンクは初心者コースで、イエローリフト横にキッズスキースクール専用エリアがあります。ドラゴンパークは全長500mのテレインパークです。' },
    ],
    snowHeading: '雪と混雑',
    snowBody: '大関嶺高原は韓国で最も寒い地域のひとつで、1月から2月上旬に雪面が最も安定します。オープンは人工降雪で、東風の日に東海側から湿った自然雪がまとまって降ります。週末とソルラル連休はリフトもシャトルも混むので平日を狙い、強風の日はケーブルカーとレインボーゾーンの運行を先に確認しましょう。',
    ticketHeading: 'リフト券',
    ticketBody: '2025-26シーズンの大人1日券（09:00〜17:00）は96,000ウォン、午前・午後・ナイター券は68,000ウォン、子ども（小学生以下）はそれぞれ78,000ウォン・56,000ウォンでした。2026-27の料金はシーズン前に発表されます。何度も滑るなら、龍平・ハイワン・アルペンシアなど6か所で使えるX6+シーズンパスも検討を。',
    accessHeading: 'アクセス',
    accessBody: 'ソウルからは弘大・新村・明洞・東大門発のスキーシャトル（2026年11月27日〜2027年3月1日）、仁川空港からはシーズン限定の空港シャトル（2026年12月7日〜2027年2月12日）があります。KTX珍富駅からは宿泊者シャトルが1日2便、タクシーなら約20分です。市外バスで横渓まで行きタクシーに乗る方法もあります。',
    linksHeading: '龍平をさらに詳しく',
    links: [
      { to: '/resort/yongpyong/', label: '龍平のライブカメラと積雪', icon: 'camera' },
      { to: '/season-calendar/', label: 'オープン・クローズ時期を確認', icon: 'calendar' },
    ],
    faq: [
      { q: '龍平リゾートはいつオープンしますか？', a: '2025-26シーズンは11月21日にオープンしました。2026-27のオープン日はまだ発表されておらず、通常は一部のコースから段階的に開きます。' },
      { q: '初心者でも楽しめますか？', a: 'はい。イエロー・ニューイエロー・ピンクと幅の広いメガグリーンがあり、スキースクールには英語インストラクターが担当する外国人専用クラスがあります。' },
      { q: 'オリンピックのコースを滑れますか？', a: 'レインボーゾーンが営業していれば、2018年五輪の回転・大回転が行われたレインボー1を滑れます。上級者専用で16:00に終了します。' },
    ],
  },
  zh: {
    title: '龙平度假村指南｜实时摄像头、雪道、缆车票与交通',
    description: 'Mona龙平（龙平度假村）完全指南：实时摄像头、包括5.6公里彩虹天堂在内的28条雪道、2018冬奥会赛道、缆车票价格，以及从首尔、仁川机场和KTX珍富站前往的方法。',
    keywords: '龙平滑雪场, 龙平度假村, Mona龙平, 龙平缆车票, 首尔到龙平, 龙平班车, 平昌滑雪',
    h1: '龙平度假村（Mona龙平）完全指南',
    lede: '龙平于1975年开业，是韩国第一座现代化滑雪场，位于平昌大关岭高原的发旺山山坡（雪道海拔745至1,450米），拥有28条雪道和14条缆车，是韩国最具代表性的滑雪度假村。这里有全长5.6公里的彩虹天堂，以及举办过2018年平昌冬奥会回转和大回转的赛道，距KTX珍富站车程约20分钟。',
    factsHeading: '基本数据',
    facts: [
      { label: '地址', value: '江原特别自治道平昌郡大关岭面奥林匹克路715' },
      { label: '山顶', value: '发旺山1,458米（缆车山顶站），雪道最高点1,450米' },
      { label: '雪道与缆车', value: '28条雪道／14条缆车（含发旺山观光缆车）' },
      { label: '最长雪道', value: '彩虹天堂5.6公里（落差705米）' },
      { label: '奥运', value: '2018年平昌冬奥会高山滑雪技术项目（彩虹1号）' },
      { label: '最近车站', value: 'KTX珍富（五台山）站，打车约20分钟' },
    ],
    zonesHeading: '各雪区特点',
    zones: [
      { name: '彩虹区', body: '山顶的高级雪道区。彩虹1至3号是国际雪联认证的高级雪道，2018年冬奥会回转和大回转在彩虹1号举行；彩虹天堂（5.6公里）是从山顶下来的长距离中级道。16:00关闭。' },
      { name: '黄金与白银区', body: '集中了又长又宽的中级道：黄金天堂（1,520米）、黄金梦幻（1,850米）和白银天堂（1,460米）。新黄金道和白银道仅限高级滑雪者。' },
      { name: '红色与绿色区', body: '从山脚就能看到的标志性红色雪道（专家级），以及宽180米的超级绿道，由六人高速缆车服务，适合初中级。' },
      { name: '黄色、粉红与龙公园', body: '黄色、新黄色和粉红是初级道，黄色缆车旁设有儿童滑雪学校专区。龙公园是一座500米长的地形公园。' },
    ],
    snowHeading: '雪况与人流',
    snowBody: '大关岭高原是韩国最冷的地区之一，1月至2月上旬雪面最稳定。雪场开放靠人工造雪，刮东风时会从东海一侧带来大量自然降雪。周末和春节假期缆车和班车都很拥挤，尽量选工作日；大风天请先确认缆车和彩虹区是否运行。',
    ticketHeading: '缆车票',
    ticketBody: '2025-26雪季成人全天票（09:00–17:00）为96,000韩元，上午、下午或夜场票68,000韩元；儿童（小学及以下）分别为78,000韩元和56,000韩元。2026-27价格将在雪季前公布。经常滑雪的话，可以考虑适用于龙平、High1、阿尔卑西亚等六家雪场的X6+季卡。',
    accessHeading: '交通方式',
    accessBody: '从首尔有弘大、新村、明洞、东大门出发的滑雪班车（2026年11月27日至2027年3月1日）；从仁川机场有雪季限定班车（2026年12月7日至2027年2月12日）。KTX珍富站有住客班车每天2班，打车约20分钟。也可以坐长途巴士到横溪再打车。',
    linksHeading: '了解更多龙平信息',
    links: [
      { to: '/resort/yongpyong/', label: '龙平实时摄像头与积雪', icon: 'camera' },
      { to: '/season-calendar/', label: '查看开放与关闭时间', icon: 'calendar' },
    ],
    faq: [
      { q: '龙平度假村什么时候开放？', a: '2025-26雪季于11月21日开放。2026-27开放日期尚未公布，通常先开放部分雪道，再分阶段开放其余雪道。' },
      { q: '初学者适合去龙平吗？', a: '适合。有黄色、新黄色、粉红雪道和宽阔的超级绿道，滑雪学校还开设由英语教练负责的外国人专班。' },
      { q: '可以滑奥运赛道吗？', a: '彩虹区开放时可以滑彩虹1号，即2018年冬奥会回转和大回转的赛道。仅限高级滑雪者，16:00关闭。' },
    ],
  },
};

export const YONGPYONG_GUIDE_COPY: Record<KrLang, FlagshipGuideCopy> = {
  ko: {
    title: '용평 여행 가이드 | 교통·숙소·식사·일정 짜기',
    description: '인천공항·서울·KTX 진부역에서 용평 가는 법, 호텔과 콘도 고르기, 리조트와 횡계의 식사, 5일 스키 일정까지 용평 여행을 짜는 실용 가이드입니다.',
    keywords: '용평 여행, 용평 가는 법, 용평 숙소, 용평 셔틀, 진부역 용평, 횡계 맛집',
    h1: '용평 스키 여행 가이드',
    lede: '용평은 공항 직행 셔틀과 KTX가 모두 닿고, 리조트 안에 숙소와 식당이 모여 있어 처음 가는 사람도 일정을 짜기 쉽습니다. 교통, 숙소, 식사, 일정 순서로 정리했습니다.',
    sections: [
      {
        icon: 'plane',
        heading: '교통: 공항 셔틀이나 KTX',
        body: [
          '인천공항에서 바로 간다면 시즌 한정 공항 셔틀(2026년 12월 7일~2027년 2월 12일)이 가장 편합니다. 제2터미널을 거쳐 제1터미널에서 출발합니다. 서울에 묵는다면 홍대·신촌·명동·동대문에서 출발하는 스키 셔틀(2026년 11월 27일~2027년 3월 1일)을 이용하세요.',
          'KTX 강릉선을 타면 진부(오대산)역에서 내립니다. 리조트 투숙객 셔틀이 하루 2회(진부역 출발 11:00·14:30 기준) 다니고, 택시로는 약 20분입니다. 셔틀 시간은 KTX 시간표에 따라 바뀌니 리조트에 확인하세요.',
        ],
      },
      {
        icon: 'hotel',
        heading: '숙소: 호텔, 콘도, 호스텔',
        body: [
          '드래곤밸리호텔은 슬로프 바로 앞의 호텔이고, 타워콘도·빌라콘도·그린피아콘도는 주방이 딸린 객실이 있어 가족이나 단체에 좋습니다. 버치힐·베르데힐·포레스트·아폴리스 같은 프리미엄 콘도도 있습니다.',
          '예산을 아끼려면 리조트 안의 호스텔 블리스힐 스테이를 고려하세요. 진부역 셔틀이 블리스힐 스테이에도 정차합니다. 리프트권 포함 패키지는 리조트 공식 사이트에서 확인할 수 있습니다.',
        ],
      },
      {
        icon: 'utensils',
        heading: '식사: 황태와 오삼불고기',
        body: [
          '리조트 안에서는 드래곤밸리호텔 1층의 더 샬레(한식·양식, 조식 뷔페), 한우 구이집 삼부자1983, 타워플라자 푸드코트를 이용할 수 있습니다. 푸드코트에는 할랄 인증 터키 음식점 에페스 케밥도 있습니다.',
          '저녁에는 아랫마을 횡계로 내려가 보세요. 황태해장국과 황태구이로 유명한 황태회관, 오삼불고기 식당이 모인 거리가 있습니다. 인기 식당은 주말 저녁에 줄이 길어 이른 시간에 가는 편이 좋습니다.',
        ],
      },
      {
        icon: 'calendar',
        heading: '5일 일정 예시',
        body: [
          '1일차: 인천공항 셔틀로 도착해 렌탈을 마치고, 19:00~23:00 야간 스키로 몸을 풉니다.',
          '2일차: 아침 일찍 발왕산 케이블카로 정상에 올라 레인보우 파라다이스(5.6km)를 탑니다. 레인보우 구역은 16:00에 닫으니 오전에 집중하세요.',
          '3일차: 골드·실버 구역의 긴 중급 코스를 돌고, 오후에는 용평워터파크에서 쉽니다.',
          '4일차: 휴식일. KTX로 강릉에 가서 바다와 커피 거리를 둘러보거나 대관령 양떼목장을 다녀옵니다.',
          '5일차: 오전만 타고 셔틀로 서울이나 공항으로 이동합니다. 주말 오후 영동고속도로 상행선은 정체가 잦으니 여유 있게 출발하세요.',
        ],
      },
    ],
    linksHeading: '관련 페이지',
    links: [
      { to: '/resort/yongpyong/', label: '용평 라이브 캠과 적설' },
      { to: '/getting-around/', label: '공항·서울에서 스키장 가는 법' },
    ],
  },
  en: {
    title: 'Yongpyong Travel Guide: Access, Lodging, Food and a 5-Day Plan',
    description: 'How to reach Yongpyong from Incheon Airport, Seoul or KTX Jinbu, choosing between the hotel and condos, eating on the resort and in Hoenggye, and a practical five-day ski plan.',
    keywords: 'yongpyong travel guide, how to get to yongpyong, yongpyong hotel, yongpyong shuttle, jinbu station yongpyong, hoenggye restaurants',
    h1: 'Yongpyong ski trip guide',
    lede: 'Yongpyong is easy to plan: a direct airport shuttle and the KTX both reach it, and the lodging and restaurants sit together on the resort. Here it is in order: getting there, where to sleep, what to eat and how to shape the days.',
    sections: [
      {
        icon: 'plane',
        heading: 'Getting there: airport shuttle or KTX',
        body: [
          'Straight from Incheon, the season-only airport shuttle (December 7, 2026 to February 12, 2027) is easiest; it calls at Terminal 2, then Terminal 1. Staying in Seoul first? Ski shuttles leave Hongdae, Sinchon, Myeongdong and Dongdaemun (November 27, 2026 to March 1, 2027).',
          'On the KTX Gangneung line, get off at Jinbu (Odaesan). A guest shuttle runs twice a day (11:00 and 14:30 from Jinbu on the posted timetable), or a taxi takes about 20 minutes. The shuttle follows the KTX timetable, so confirm times with the resort.',
        ],
      },
      {
        icon: 'hotel',
        heading: 'Lodging: hotel, condos or hostel',
        body: [
          'Dragon Valley Hotel sits right by the slopes, while Tower Condo, Villa Condo and Green Pia Condo have rooms with kitchens, good for families and groups. Premium condos include Birch Hill, Verde Hill, Forest and Apolis.',
          'On a budget, the on-resort hostel Bliss Hill Stay is the option; the Jinbu shuttle stops there too. Lift-ticket packages are listed on the resort\'s own website.',
        ],
      },
      {
        icon: 'utensils',
        heading: 'Food: dried pollack and osam bulgogi',
        body: [
          'On the resort: The Chalet on the first floor of Dragon Valley Hotel (Korean and Western, breakfast buffet), the Korean-beef grill Sambuja 1983 and the Tower Plaza food court, which includes the halal-certified Turkish outlet Efes Kebab.',
          'For dinner, head down to Hoenggye: Hwangtae Hoegwan is known for dried-pollack hangover soup and grilled pollack, and there is a whole street of osam bulgogi (squid and pork belly) restaurants. Popular places queue on weekend evenings, so go early.',
        ],
      },
      {
        icon: 'calendar',
        heading: 'A five-day plan',
        body: [
          'Day 1: Arrive on the airport shuttle, sort rentals, and warm up under the lights from 19:00 to 23:00.',
          'Day 2: Take the Balwangsan cable car early and ride Rainbow Paradise (5.6 km). The Rainbow zone closes at 16:00, so focus on it in the morning.',
          'Day 3: Lap the long intermediate runs in the Gold and Silver zones, then rest at Yongpyong Water Park in the afternoon.',
          'Day 4: Rest day: KTX to Gangneung for the sea and coffee street, or visit Daegwallyeong Sheep Farm.',
          'Day 5: Ski the morning, then shuttle back to Seoul or the airport. The Yeongdong Expressway towards Seoul often jams on weekend afternoons, so leave plenty of time.',
        ],
      },
    ],
    linksHeading: 'Related pages',
    links: [
      { to: '/resort/yongpyong/', label: 'Yongpyong live webcams and snow data' },
      { to: '/getting-around/', label: 'Getting there from the airport and Seoul' },
    ],
  },
  ja: {
    title: '龍平 旅行ガイド｜アクセス・宿・食事・日程の組み方',
    description: '仁川空港・ソウル・KTX珍富駅から龍平への行き方、ホテルとコンドの選び方、リゾートと横渓での食事、5日間のスキー日程まで、龍平旅行を組み立てる実用ガイドです。',
    keywords: '龍平 旅行, 龍平 行き方, 龍平 ホテル, 龍平 シャトル, 珍富駅 龍平, 横渓 グルメ',
    h1: '龍平スキー旅行ガイド',
    lede: '龍平は空港直行シャトルとKTXの両方で行け、リゾート内に宿と食事処が集まっているので、初めてでも日程を組みやすい場所です。交通、宿、食事、日程の順にまとめました。',
    sections: [
      {
        icon: 'plane',
        heading: 'アクセス：空港シャトルかKTX',
        body: [
          '仁川空港から直行するならシーズン限定の空港シャトル（2026年12月7日〜2027年2月12日）が最も楽です。第2ターミナルを経て第1ターミナルから出発します。ソウルに泊まるなら弘大・新村・明洞・東大門発のスキーシャトル（2026年11月27日〜2027年3月1日）を。',
          'KTX江陵線なら珍富（オデサン）駅で下車。宿泊者シャトルが1日2便（掲載時刻では珍富駅発11:00・14:30）あり、タクシーなら約20分です。シャトルの時刻はKTXのダイヤに合わせて変わるのでリゾートに確認してください。',
        ],
      },
      {
        icon: 'hotel',
        heading: '宿：ホテル、コンド、ホステル',
        body: [
          'ドラゴンバレーホテルはゲレンデ目の前のホテル。タワーコンド、ヴィラコンド、グリーンピアコンドにはキッチン付きの客室があり、家族やグループ向きです。バーチヒル、ベルデヒル、フォレスト、アポリスなどのプレミアムコンドもあります。',
          '予算を抑えるならリゾート内のホステル「ブリスヒル・ステイ」を。珍富駅のシャトルもここに停まります。リフト券付きパッケージはリゾート公式サイトで確認できます。',
        ],
      },
      {
        icon: 'utensils',
        heading: '食事：ファンテとオサムプルコギ',
        body: [
          'リゾート内ではドラゴンバレーホテル1階の「ザ・シャレー」（韓国料理・洋食、朝食ビュッフェ）、韓牛焼肉の「サムブジャ1983」、タワープラザのフードコートが使えます。フードコートにはハラール認証のトルコ料理店「エフェス・ケバブ」もあります。',
          '夕食は麓の横渓へ。ファンテのスープと焼き物で知られる「ファンテ会館」や、オサムプルコギ（イカと豚バラ炒め）の店が並ぶ通りがあります。人気店は週末の夜に行列ができるので早めの時間に。',
        ],
      },
      {
        icon: 'calendar',
        heading: '5日間のモデル日程',
        body: [
          '1日目：空港シャトルで到着し、レンタルを済ませて19:00〜23:00のナイターで足慣らし。',
          '2日目：朝一番に発旺山ケーブルカーで山頂へ上がり、レインボーパラダイス（5.6km）を滑走。レインボーゾーンは16:00に終わるので午前中に集中。',
          '3日目：ゴールド・シルバーゾーンの長い中級コースを回り、午後は龍平ウォーターパークでひと休み。',
          '4日目：休養日。KTXで江陵へ行き海とコーヒー通りを巡るか、大関嶺の羊牧場へ。',
          '5日目：午前だけ滑り、シャトルでソウルか空港へ。週末午後の嶺東高速道路上りは渋滞しやすいので余裕をもって出発を。',
        ],
      },
    ],
    linksHeading: '関連ページ',
    links: [
      { to: '/resort/yongpyong/', label: '龍平のライブカメラと積雪' },
      { to: '/getting-around/', label: '空港・ソウルからスキー場への行き方' },
    ],
  },
  zh: {
    title: '龙平旅行指南｜交通、住宿、餐饮与五日行程',
    description: '从仁川机场、首尔或KTX珍富站前往龙平的方法，酒店与公寓怎么选，度假村内和横溪的餐饮，以及实用的五日滑雪行程。',
    keywords: '龙平旅行, 怎么去龙平, 龙平酒店, 龙平班车, 珍富站 龙平, 横溪美食',
    h1: '龙平滑雪旅行指南',
    lede: '龙平有机场直达班车和KTX两种方式可达，住宿和餐厅都集中在度假村内，第一次去也很容易规划。下面按交通、住宿、餐饮和行程的顺序介绍。',
    sections: [
      {
        icon: 'plane',
        heading: '交通：机场班车或KTX',
        body: [
          '从仁川机场直接前往，雪季限定的机场班车（2026年12月7日至2027年2月12日）最方便，先停T2再从T1出发。如果先住首尔，可以坐从弘大、新村、明洞、东大门出发的滑雪班车（2026年11月27日至2027年3月1日）。',
          '乘KTX江陵线在珍富（五台山）站下车。住客班车每天2班（按公布时刻表珍富站11:00和14:30发车），打车约20分钟。班车时间随KTX时刻表调整，请向度假村确认。',
        ],
      },
      {
        icon: 'hotel',
        heading: '住宿：酒店、公寓或青年旅舍',
        body: [
          '龙谷酒店就在雪道旁；塔楼公寓、别墅公寓和Green Pia公寓有带厨房的客房，适合家庭和团体。还有Birch Hill、Verde Hill、Forest、Apolis等高级公寓。',
          '预算有限的话可以选择度假村内的青年旅舍Bliss Hill Stay，珍富站班车也停靠这里。含缆车票的套餐可在度假村官网查询。',
        ],
      },
      {
        icon: 'utensils',
        heading: '餐饮：黄太鱼与炒鱿鱼五花肉',
        body: [
          '度假村内可以去龙谷酒店一层的The Chalet（韩餐、西餐和早餐自助）、韩牛烤肉店三父子1983，以及Tower Plaza美食广场，那里还有清真认证的土耳其餐厅Efes Kebab。',
          '晚餐可以下山到横溪：黄太会馆以黄太醒酒汤和烤黄太闻名，还有一整条炒鱿鱼五花肉（오삼불고기）餐馆街。热门店周末晚上要排队，建议早点去。',
        ],
      },
      {
        icon: 'calendar',
        heading: '五日行程示例',
        body: [
          '第1天：乘机场班车抵达，办好租赁，19:00至23:00滑夜场热身。',
          '第2天：一早乘发旺山缆车上山顶，滑彩虹天堂（5.6公里）。彩虹区16:00关闭，上午集中滑。',
          '第3天：在黄金区和白银区的长距离中级道巡航，下午去龙平水上乐园休息。',
          '第4天：休息日。乘KTX去江陵看海、逛咖啡街，或去大关岭牧羊场。',
          '第5天：上午滑雪，然后乘班车返回首尔或机场。周末下午岭东高速公路往首尔方向常堵车，请预留充足时间。',
        ],
      },
    ],
    linksHeading: '相关页面',
    links: [
      { to: '/resort/yongpyong/', label: '龙平实时摄像头与积雪' },
      { to: '/getting-around/', label: '从机场和首尔前往滑雪场' },
    ],
  },
};

export const YONGPYONG_FAQ_COPY: Record<KrLang, FlagshipFaqCopy> = {
  ko: {
    title: '용평리조트 자주 묻는 질문 | 슬로프·요금·교통',
    description: '용평의 개장 시기, 리프트권과 렌탈 요금, 초보자·가족 적합도, 영어 강습, 올림픽 코스, 셔틀과 KTX, 케이블카와 워터파크에 대한 질문에 답합니다.',
    keywords: '용평 자주 묻는 질문, 용평 리프트권 가격, 용평 셔틀버스, 용평 초보자, 용평 케이블카',
    h1: '용평리조트 자주 묻는 질문',
    lede: '용평을 예약하기 전에 가장 많이 묻는 질문들을 정리했습니다.',
    faq: [
      { q: '용평은 언제 개장하나요?', a: '2025-26 시즌에는 11월 21일 핑크 슬로프부터 개장해 나머지 슬로프를 단계적으로 열었습니다. 2026-27 개장일은 아직 발표되지 않았습니다.' },
      { q: '리프트권은 얼마인가요?', a: '2025-26 시즌 성인 주간권 96,000원, 오전·오후·야간권 68,000원, 어린이 주간권 78,000원이었습니다. 2026-27 요금은 시즌 전에 공지됩니다.' },
      { q: '슬로프는 몇 개인가요?', a: '리조트 공식 자료 기준 28개 슬로프와 14기의 리프트(발왕산 관광 케이블카 포함)가 있습니다. 가장 긴 레인보우 파라다이스는 5.6km입니다.' },
      { q: '영어 강습이 있나요?', a: '모나용평 스키스쿨에 영어가 가능한 강사가 맡는 외국인 전담반이 있습니다(033-330-7373). 리조트 인증 스쿨 외 개인 강습은 허용되지 않습니다.' },
      { q: '아이와 함께 가도 좋나요?', a: '옐로·뉴옐로·핑크 초보자 코스와 옐로 리프트 옆 어린이 스키스쿨 전용 공간이 있고, 눈썰매장과 워터파크도 있어 가족 여행에 좋습니다.' },
      { q: '야간 스키가 있나요?', a: '네. 19:00~23:00에 운영하며 17:00~19:00은 정설 시간입니다. 레인보우 구역은 주간에만 운영합니다(16:00 마감).' },
      { q: '인천공항에서 어떻게 가나요?', a: '시즌 한정 공항 셔틀이 2026년 12월 7일부터 2027년 2월 12일까지 운행합니다. 그 외에는 공항철도로 서울역에 가서 KTX를 타고 진부역에서 내리세요.' },
      { q: '렌탈 요금은 얼마인가요?', a: '2025-26 리조트 직영 렌탈 기준 일반 풀세트가 주간 48,000원, 반일·야간 40,000원이었고 의류 상하는 1회 20,000원부터였습니다.' },
      { q: '발왕산 케이블카는 스키어가 아니어도 탈 수 있나요?', a: '네. 국내 최장인 3.7km 케이블카로 편도 약 18분이며 성인 왕복 27,000원입니다. 정상에 스카이워크와 천년주목숲길이 있습니다. 매월 둘째·넷째 월요일과 강풍 시에는 운휴합니다.' },
      { q: '스키를 쉬는 날에는 무엇을 하나요?', a: '용평워터파크, 발왕산 케이블카, 대관령 양떼목장, KTX로 가는 강릉의 바다와 커피 거리를 추천합니다.' },
    ],
  },
  en: {
    title: 'Yongpyong FAQ: Slopes, Prices and Getting There',
    description: 'Yongpyong answered: when it opens, lift ticket and rental prices, beginners and families, English lessons, the Olympic course, shuttles and KTX, the cable car and the water park.',
    keywords: 'yongpyong faq, yongpyong lift ticket price, yongpyong shuttle bus, yongpyong for beginners, yongpyong english ski lessons',
    h1: 'Yongpyong Resort FAQ',
    lede: 'The questions people actually ask before booking Yongpyong.',
    faq: [
      { q: 'When does Yongpyong open?', a: 'In 2025-26 it opened on November 21 with the Pink slope and added the rest in stages. The 2026-27 date has not been announced.' },
      { q: 'How much are lift tickets?', a: 'In 2025-26 an adult day ticket was ₩96,000, a morning, afternoon or night ticket ₩68,000 and a child day ticket ₩78,000. 2026-27 prices will be announced before the season.' },
      { q: 'How many slopes are there?', a: 'The resort lists 28 slopes and 14 lifts, including the Balwangsan cable car. The longest, Rainbow Paradise, is 5.6 km.' },
      { q: 'Are there English ski lessons?', a: 'Yes. Mona Yongpyong Ski School runs a foreigners\' class with English-speaking instructors (033-330-7373). Only resort-certified schools may teach.' },
      { q: 'Is it good for children?', a: 'Yes: beginner slopes (Yellow, New Yellow, Pink), a dedicated kids\' ski-school area by the Yellow lift, a sledding hill and a water park.' },
      { q: 'Is there night skiing?', a: 'Yes, 19:00 to 23:00, with grooming from 17:00 to 19:00. The Rainbow zone is day-only and closes at 16:00.' },
      { q: 'How do I get there from Incheon Airport?', a: 'A season-only airport shuttle runs from December 7, 2026 to February 12, 2027. Otherwise take AREX to Seoul Station, then the KTX to Jinbu.' },
      { q: 'How much do rentals cost?', a: 'At the resort\'s own shop in 2025-26 a standard set was ₩48,000 for the day or ₩40,000 for a half-day or night session; jacket and pants started at ₩20,000.' },
      { q: 'Can non-skiers ride the Balwangsan cable car?', a: 'Yes. At 3.7 km it is Korea\'s longest, about 18 minutes each way, ₩27,000 adult return. The top has a skywalk and an old-growth yew forest trail. It closes on the 2nd and 4th Monday of each month and in high wind.' },
      { q: 'What is there to do on a non-ski day?', a: 'Yongpyong Water Park, the Balwangsan cable car, Daegwallyeong Sheep Farm, or the KTX to Gangneung for the sea and coffee street.' },
    ],
  },
  ja: {
    title: '龍平リゾート よくある質問｜コース・料金・アクセス',
    description: '龍平のオープン時期、リフト券とレンタルの料金、初心者・家族向けか、英語レッスン、五輪コース、シャトルとKTX、ケーブルカーとウォーターパークについての質問に答えます。',
    keywords: '龍平 よくある質問, 龍平 リフト券 料金, 龍平 シャトルバス, 龍平 初心者, 龍平 英語 レッスン',
    h1: '龍平リゾート よくある質問',
    lede: '龍平を予約する前によく聞かれる質問をまとめました。',
    faq: [
      { q: '龍平はいつオープンしますか？', a: '2025-26シーズンは11月21日にピンクコースから営業を始め、残りを段階的に開きました。2026-27のオープン日はまだ発表されていません。' },
      { q: 'リフト券はいくらですか？', a: '2025-26シーズンは大人1日券96,000ウォン、午前・午後・ナイター券68,000ウォン、子ども1日券78,000ウォンでした。2026-27の料金はシーズン前に発表されます。' },
      { q: 'コースはいくつありますか？', a: 'リゾートの公式資料では28コースと14基のリフト（発旺山観光ケーブルカーを含む）です。最長のレインボーパラダイスは5.6kmです。' },
      { q: '英語のレッスンはありますか？', a: 'あります。モナ龍平スキースクールに英語の話せるインストラクターが担当する外国人専用クラスがあります（033-330-7373）。認定スクール以外の個人レッスンは認められていません。' },
      { q: '子ども連れでも楽しめますか？', a: '初心者コース（イエロー、ニューイエロー、ピンク）、イエローリフト横のキッズスクール専用エリア、そり場、ウォーターパークがあり家族向きです。' },
      { q: 'ナイターはありますか？', a: 'あります。19:00〜23:00で、17:00〜19:00は整備時間です。レインボーゾーンは日中のみ（16:00終了）です。' },
      { q: '仁川空港からはどう行きますか？', a: 'シーズン限定の空港シャトルが2026年12月7日〜2027年2月12日に運行します。それ以外は空港鉄道でソウル駅へ行き、KTXで珍富駅へ。' },
      { q: 'レンタル料金は？', a: '2025-26のリゾート直営レンタルで、標準セットが1日48,000ウォン、半日・ナイター40,000ウォン、ウェア上下は1回20,000ウォンからでした。' },
      { q: 'スキーをしなくても発旺山ケーブルカーに乗れますか？', a: '乗れます。韓国最長の3.7kmで片道約18分、大人往復27,000ウォン。山頂にはスカイウォークと樹齢の長いイチイの森の散策路があります。毎月第2・第4月曜と強風時は運休です。' },
      { q: '滑らない日は何をすればいいですか？', a: '龍平ウォーターパーク、発旺山ケーブルカー、大関嶺の羊牧場、KTXで行く江陵の海とコーヒー通りがおすすめです。' },
    ],
  },
  zh: {
    title: '龙平度假村常见问题｜雪道、价格与交通',
    description: '解答龙平的开放时间、缆车票和租赁价格、是否适合初学者和家庭、英语教学、奥运赛道、班车与KTX、缆车和水上乐园等问题。',
    keywords: '龙平常见问题, 龙平缆车票价格, 龙平班车, 龙平初学者, 龙平英语滑雪课',
    h1: '龙平度假村常见问题',
    lede: '预订龙平之前大家最常问的问题。',
    faq: [
      { q: '龙平什么时候开放？', a: '2025-26雪季于11月21日从粉红雪道开始营业，其余雪道分阶段开放。2026-27开放日期尚未公布。' },
      { q: '缆车票多少钱？', a: '2025-26雪季成人全天票96,000韩元，上午、下午或夜场票68,000韩元，儿童全天票78,000韩元。2026-27价格将在雪季前公布。' },
      { q: '有多少条雪道？', a: '度假村官方资料为28条雪道和14条缆车（含发旺山观光缆车）。最长的彩虹天堂全长5.6公里。' },
      { q: '有英语滑雪课吗？', a: '有。Mona龙平滑雪学校开设由会说英语的教练负责的外国人专班（033-330-7373）。只有度假村认证的学校可以授课。' },
      { q: '适合带孩子去吗？', a: '适合。有初级道（黄色、新黄色、粉红）、黄色缆车旁的儿童滑雪学校专区、雪橇场和水上乐园。' },
      { q: '有夜场吗？', a: '有，19:00至23:00，17:00至19:00为压雪时间。彩虹区只在白天开放（16:00关闭）。' },
      { q: '从仁川机场怎么去？', a: '雪季限定机场班车于2026年12月7日至2027年2月12日运行。其他时间可乘机场铁路到首尔站，再坐KTX到珍富站。' },
      { q: '租赁多少钱？', a: '2025-26雪季度假村直营租赁店标准套装全天48,000韩元，半天或夜场40,000韩元，雪服上下装每次20,000韩元起。' },
      { q: '不滑雪也能坐发旺山缆车吗？', a: '可以。全长3.7公里，是韩国最长的缆车，单程约18分钟，成人往返27,000韩元。山顶有天空步道和古紫杉林步道。每月第二、第四个星期一及大风天停运。' },
      { q: '不滑雪的日子可以做什么？', a: '推荐龙平水上乐园、发旺山缆车、大关岭牧羊场，或乘KTX去江陵看海、逛咖啡街。' },
    ],
  },
};

/** Generic aliases for the shared flagship page set. */
export const FLAGSHIP_PAGE_COPY = YONGPYONG_PAGE_COPY;
export const FLAGSHIP_GUIDE_COPY = YONGPYONG_GUIDE_COPY;
export const FLAGSHIP_FAQ_COPY = YONGPYONG_FAQ_COPY;
